'use client';

import React from 'react';

import { Icon } from '@benzinga/core-ui';
import { AuthenticationManager } from '@benzinga/session';
import { SessionContext } from '@benzinga/session-context';
import i18n from '@benzinga/translate';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope';
import { useTranslation } from 'react-i18next';

import styles from './EmailVerificationBanner.module.scss';

const profileNs = 'profile' as const;

export interface EmailVerificationBannerProps {
  email: string;
}

export const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({ email }) => {
  const session = React.useContext(SessionContext);
  const { t } = useTranslation(['profile', 'common'], { i18n });
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'sent' | 'error'>('idle');

  const onVerifyNow = React.useCallback(async () => {
    setStatus('loading');
    try {
      const result = await session.getManager(AuthenticationManager).emailRequest();
      if (result.err) {
        setStatus('error');
        return;
      }
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  }, [session]);

  return (
    <div className={styles.banner} data-testid="email-verification-banner" id="email-verification-banner">
      <div className={styles.main}>
        <Icon className={styles.icon} icon={faEnvelope} />
        <p className={styles.copyLine}>
          <span className={styles.headline}>{t('Profile.email-verification-banner-headline', { ns: profileNs })}</span>{' '}
          <span className={styles.emailLabel}>
            {t('Profile.email-verification-banner-your-email', { ns: profileNs })}
          </span>{' '}
          <span className={styles.email}>{email}</span>
        </p>
      </div>
      <div className={styles.actions}>
        <button
          className={styles.verifyCta}
          data-action="Email verification banner — verify now"
          disabled={status === 'loading' || status === 'sent'}
          onClick={onVerifyNow}
          type="button"
        >
          {status === 'sent' ? (
            <>{t('Profile.verification-email-sent', { ns: profileNs })}</>
          ) : status === 'loading' ? (
            <>{t('Profile.email-verification-banner-sending', { ns: profileNs })}</>
          ) : status === 'error' ? (
            <>
              <span className={styles.verifyCtaStatus}>
                {t('Profile.email-verification-banner-unverified', { ns: profileNs })}
              </span>
              <span className={styles.verifyCtaDivider}>—</span>
              <span className={styles.verifyCtaLabel}>
                {t('Profile.email-verification-banner-try-again', { ns: profileNs })}
              </span>
            </>
          ) : (
            <>
              <span className={styles.verifyCtaStatus}>
                {t('Profile.email-verification-banner-unverified', { ns: profileNs })}
              </span>
              <span className={styles.verifyCtaDivider}>—</span>
              <span className={styles.verifyCtaLabel}>{t('Profile.verified', { ns: profileNs })}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
